import { DateAdapter } from '@/data/protocols'
import { ClientMonitoringType, SigmaCloudKeyResponseModel, SowillIdentifier } from '@/domain/models'
import { GetSigmaEvents, SigmaFilterType } from '@/domain/usecases'

type MonitoringPeriod = {
  dataInicial: string
  dataFinal: string
}

type MonitoringKeyParams = {
  clientType: ClientMonitoringType
  currentSigmaKey: SigmaCloudKeyResponseModel
  remotyIdEquipamento: number
  sowillIdentifier?: SowillIdentifier
}

type MonitoringEventsKeyParams = MonitoringKeyParams & {
  dateAdapter: DateAdapter
  filters: MonitoringPeriod
  sigmaFilterType: SigmaFilterType | null
}

const buildMonitoringSelector = ({
  clientType,
  currentSigmaKey,
  remotyIdEquipamento,
  sowillIdentifier
}: MonitoringKeyParams): Record<string, string | number | undefined> | undefined => {
  switch (clientType) {
    case ClientMonitoringType.REMOTY:
      return { remotyIdEquipamento }
    case ClientMonitoringType.SIGMA_CLOUD:
    case ClientMonitoringType.SIGMA_DESK:
      return {
        codCliente: currentSigmaKey.codCliente,
        empresaSigma: currentSigmaKey.empresaSigma,
        chave: currentSigmaKey.chave,
        particao: currentSigmaKey.particao
      }
    case ClientMonitoringType.SO_WILL:
      return {
        codInternoChave: sowillIdentifier?.codInternoChave,
        idCliente: sowillIdentifier?.idCliente
      }
    default:
      return undefined
  }
}

export const buildMonitoringTabRequestKey = (params: MonitoringKeyParams): string => JSON.stringify({
  connectionType: params.clientType,
  selector: buildMonitoringSelector(params)
})

export const resolveMonitoringPeriod = (
  filters: MonitoringPeriod,
  dateAdapter: DateAdapter
): MonitoringPeriod => ({
  dataInicial: filters.dataInicial && filters.dataInicial !== ''
    ? filters.dataInicial
    : dateAdapter.format(dateAdapter.daysAgo(GetSigmaEvents.RN.DEFAULT_DAYS).toString(), 'YYYY-MM-DD'),
  dataFinal: filters.dataFinal && filters.dataFinal !== ''
    ? filters.dataFinal
    : dateAdapter.format(dateAdapter.today(), 'YYYY-MM-DD')
})

export const buildMonitoringEventsRequestKey = ({
  dateAdapter,
  filters,
  sigmaFilterType,
  ...params
}: MonitoringEventsKeyParams): string => {
  const period = resolveMonitoringPeriod(filters, dateAdapter)

  return JSON.stringify({
    connectionType: params.clientType,
    selector: buildMonitoringSelector(params),
    period,
    sigmaFilter: [ClientMonitoringType.SIGMA_CLOUD, ClientMonitoringType.SIGMA_DESK].includes(params.clientType)
      ? sigmaFilterType
      : undefined
  })
}
